"use client";

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LeftOutlined, LockOutlined, RightOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { IBookTypeData } from "../../apiservice/userService.type";

interface BookSliderProps {
  title: string;
  books: IBookTypeData[];
  showTags?: boolean;
  showCategories?: boolean;
}

const BookSlider = ({
  title,
  books,
  showTags = false,
  showCategories = false,
}: BookSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Check if we need to show navigation arrows
  useEffect(() => {
    const checkScroll = () => {
      if (!sliderRef.current) return;

      setShowLeftArrow(sliderRef.current.scrollLeft > 0);
      setShowRightArrow(
        sliderRef.current.scrollLeft <
          sliderRef.current.scrollWidth - sliderRef.current.clientWidth - 10
      );
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener("scroll", checkScroll);
      // Initial check
      checkScroll();
    }

    return () => {
      if (slider) {
        slider.removeEventListener("scroll", checkScroll);
      }
    };
  }, [books]);

  // Scroll functions
  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  if (!books || books.length === 0) {
    return null;
  }

  return (
    <div className="relative my-6">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-bold tracking-tight text-gray-900 dark:text-stone-100">{title}</h2>
        <div className="flex gap-2">
          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll left"
            >
              <LeftOutlined />
            </button>
          )}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll right"
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </div>

      <div
        ref={sliderRef}
        className="flex overflow-x-auto pb-4 gap-4 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {(books || []).map((book) => (
          <Link
            key={book.id}
            to={`/users/book-details/${book.id}/${encodeURIComponent(
              book.title.replace(/\s+/g, "_")
            )}`}
            className="flex-shrink-0 w-40 group"
          >
            <div className="h-full flex flex-col">
              <div className="relative h-60 rounded-2xl overflow-hidden shadow-md bg-gray-100 dark:bg-stone-700">
                <img
                  src={book.coverImageUrl || "/placeholder.svg"}
                  alt={book.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {book.isNewRelease && (
                  <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full shadow">
                    New
                  </div>
                )}

                 {!book.hasAccess && (
                  <div className="absolute bottom-2 right-2 w-8 h-8 flex items-center justify-center bg-black/60 backdrop-blur-sm text-white rounded-full">
                    <LockOutlined style={{ fontSize: '14px', color: '#fff' }} />
                  </div>
                )}
              </div>

              <div className="pt-2 px-1 flex flex-col flex-grow">
                <h3 className="font-semibold text-sm line-clamp-2 mb-1 text-gray-900 dark:text-stone-100">
                  {book.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-stone-400 mb-2">{book.authorName}</p>

                {showTags && book.tags && book.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {book.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-stone-700 text-gray-800 dark:text-stone-200 rounded-full"
                      >
                        {tag.title}
                      </span>
                    ))}
                  </div>
                )}

                {showCategories &&
                  book.categories &&
                  book.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-auto">
                      {book.categories.slice(0, 1).map((category) => (
                        <Tag key={category.id} color="blue" className="text-xs">
                          {category.bookCategory?.title}
                        </Tag>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default BookSlider;
